import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import type { CountingProblem, PlayedCard } from '../../types/counting';
import { PlayableHandDisplay } from './PlayableHandDisplay';
import { PlayedCardsDisplay } from './PlayedCardsDisplay';
import { TrickCountIndicator } from './TrickCountIndicator';
import { TrickNavigationBar } from './TrickNavigationBar';
import { AnimationSpeedControl } from './AnimationSpeedControl';
import { InitialPlayButton } from './InitialPlayButton';
import { useTrickAnimation } from './useTrickAnimation';
import type { UseTrickAnimationReturn } from './useTrickAnimation';
import { CountingTable } from './CountingTable';
import type { CountingTableSeat } from './CountingTable';
import { AuctionTable } from '../problems/AuctionTable';
import { VulnerabilityIndicator } from '../problems/VulnerabilityIndicator';
import type { AuctionBid } from '../../types/problems';

interface PlayTableProps {
  problem: CountingProblem;
  /** Callback to notify parent when animation reaches stop trick */
  onAnimationComplete?: () => void;
  /** Callback to notify parent of animation state changes */
  onAnimationStateChange?: (state: 'INITIAL' | 'PLAYING' | 'STOPPED' | 'COMPLETED') => void;
  /** When true, short-phone layout places controls below hands instead of above */
  isHowardPage?: boolean;
  /** If true, skip INITIAL state and auto-start animation (for E2E tests) */
  autoplay?: boolean;
  /** Callback when the "Claimed" indicator becomes visible (last trick of a claimed game reached) */
  onClaimedVisible?: () => void;
  /**
   * Whether the table renders its own auction.
   *
   * Legacy: /howard-hands and /ask-jeff-review have no frame around the table and rely on it for
   * the auction. Rebuilt Counting table: the page passes `false` on desktop, where the frame draws
   * the auction in a panel of its own above the answer, and `true` below that, where the table
   * draws it beside the board.
   */
  showAuction?: boolean;
  /**
   * Whether the table renders its own controls row — trick navigation and animation speed.
   *
   * Meaningful to the LEGACY layouts only, for the same reason as `showAuction`. The rebuilt
   * Counting table always draws its controls bar: the controls belong to the trick they scrub
   * through, and the frame's own controls slot is not used.
   */
  showControls?: boolean;
  /**
   * An externally-owned trick animation (feature 028).
   *
   * Counting practice hoists `useTrickAnimation` onto the page, because the question is withheld
   * until the replay reaches its stop trick and the call panel has to read the same instance the
   * table displays. Omitted by the editor pages, which let the table own its animation as before.
   */
  animation?: UseTrickAnimationReturn;
  /**
   * Which table this is (feature 028).
   *
   * `legacy` (the default) is the pre-028 table exactly as it was, and is what /howard-hands and
   * /ask-jeff-review render — both are outside 028's scope and must not change. (The preserved
   * /counting-practice-old was the third such consumer until the `_old` cleanup removed it.)
   *
   * `table-session` is the rebuilt Counting page's table, drawn by `CountingTable`: three
   * full-width bands (scoreboard, table, controls) on the 023 palette, inside the frame's hand
   * panel. See that component for the arrangement and why it is shaped that way.
   */
  variant?: 'legacy' | 'table-session';
}

/** Replace "Pass" with "P" to keep the auction table narrow. */
function abbreviateAuction(auction: AuctionBid[]): AuctionBid[] {
  return auction.map(bid => ({
    ...bid,
    call: bid.call === 'Pass' ? 'P' : bid.call,
  }));
}

/**
 * Build the complete play sequence from the problem's playedCards array.
 */
function buildPlaySequence(problem: CountingProblem): PlayedCard[] {
  return [...problem.playedCards, ...problem.currentTrickCards];
}

/**
 * Bridge play table with animated trick replay.
 *
 * Owns the trick state, the played-card bookkeeping and the navigation wiring, then draws one of
 * two tables with it:
 *
 *   `variant="legacy"`       — the pre-028 layouts below: a 3x3 desktop grid and two stacked
 *                              narrow arrangements. Hands are always horizontal (declarer left,
 *                              dummy right) whichever axis declares; the compass direction is
 *                              named only in each hand's label.
 *   `variant="table-session"` — the rebuilt Counting table (`CountingTable`), where the hands DO
 *                              sit on their own compass sides.
 */
export function PlayTable({ problem, onAnimationComplete, onAnimationStateChange, isHowardPage, autoplay = false, onClaimedVisible, showAuction = true, showControls = true, animation: externalAnimation, variant = 'legacy' }: PlayTableProps) {
  const isTableSession = variant === 'table-session';
  // Detect short phones (iPhone SE class: narrow + short viewport)
  const [isShortPhone, setIsShortPhone] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px) and (max-height: 599px)');
    setIsShortPhone(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsShortPhone(e.matches);
    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
    };
  }, []);

  const dirPairs = [problem.declarerDirection, problem.dummyDirection] as string[];
  const isNSAxis = problem.declarerDirection === 'N' || problem.declarerDirection === 'S';
  const DIRECTION_FULL_NAMES: Record<string, string> = { N: 'North', E: 'East', S: 'South', W: 'West' };
  const declarerLabel = `Declarer: ${DIRECTION_FULL_NAMES[problem.declarerDirection] ?? problem.declarerDirection}`;

  const getHandForDirection = (dir: string): CountingTableSeat | null => {
    if (!dirPairs.includes(dir)) return null;
    const isDeclarer = dir === problem.declarerDirection;
    const role = isDeclarer ? 'You hold' : 'Dummy';
    const DIRECTION_FULL: Record<string, string> = { N: 'North', E: 'East', S: 'South', W: 'West' };
    return {
      hand: isDeclarer ? problem.declarerHand : problem.dummyHand,
      label: `${role} (${DIRECTION_FULL[dir] ?? dir})`,
      mobileLabel: `${role} (${dir})`,
      direction: dir,
      role: isDeclarer ? 'declarer' : 'dummy',
    };
  };

  const westHand = getHandForDirection('W');
  const eastHand = getHandForDirection('E');
  const northHand = getHandForDirection('N');
  const southHand = getHandForDirection('S');

  const declarerHand = getHandForDirection(problem.declarerDirection);
  const dummyHandInfo = getHandForDirection(problem.dummyDirection);

  // Left/right slots in the LEGACY layouts, used regardless of axis. For an E/W declarer the
  // actual compass positions are preserved (West on the left, East on the right). For an N/S
  // declarer the declarer is placed on the left and the dummy on the right — matches the mobile
  // convention and avoids the extra vertical space a top/bottom stack needs.
  const leftHand = isNSAxis ? declarerHand : westHand;
  const rightHand = isNSAxis ? dummyHandInfo : eastHand;

  // The rebuilt table takes its two hands in COMPASS order and places them itself.
  const seats: [CountingTableSeat | null, CountingTableSeat | null] = isNSAxis
    ? [northHand, southHand]
    : [westHand, eastHand];

  const shortAuction = abbreviateAuction(problem.auction);

  const allPlayedCards = useMemo(() => buildPlaySequence(problem), [problem]);

  // Feature 028: a caller that drives the animation from outside (Counting practice, whose call
  // panel reads the same instance) passes it in here. The internal instance is still constructed —
  // hooks cannot be called conditionally — but when an external one is supplied nothing drives it,
  // so it stays in INITIAL and starts no timers.
  const internalAnimation = useTrickAnimation({
    allPlayedCards,
    stopAfterTrick: problem.stopAfterTrick,
    totalTricksPlayed: problem.totalTricksPlayed ?? 13,
    autoplay,
  });
  const animation = externalAnimation ?? internalAnimation;

  const [showClaimed, setShowClaimed] = useState(false);
  useEffect(() => {
    if (problem.claimed && animation.hasReachedLastTrick && !showClaimed) {
      setShowClaimed(true);
      onClaimedVisible?.();
    }
  }, [problem.claimed, animation.hasReachedLastTrick, showClaimed, onClaimedVisible]);

  useEffect(() => {
    onAnimationStateChange?.(animation.animationState);
  }, [animation.animationState, onAnimationStateChange]);

  useEffect(() => {
    if (animation.hasReachedStopTrick) {
      onAnimationComplete?.();
    }
  }, [animation.hasReachedStopTrick, onAnimationComplete]);

  const prevTableResultIdRef = useRef(problem.tableResultId);
  useEffect(() => {
    if (prevTableResultIdRef.current !== problem.tableResultId) {
      prevTableResultIdRef.current = problem.tableResultId;
      animation.reset();
      setShowClaimed(false);
    }
  }, [problem.tableResultId, animation]);

  const playedCardCodes = useMemo(() => {
    return animation.cardsPlayedSoFar.map(pc => pc.card.toUpperCase());
  }, [animation.cardsPlayedSoFar]);

  const declarerPlayedCards = useMemo(() => {
    return playedCardCodes.filter(code => {
      const card = animation.cardsPlayedSoFar.find(pc => pc.card.toUpperCase() === code);
      return card && card.direction === problem.declarerDirection;
    });
  }, [playedCardCodes, animation.cardsPlayedSoFar, problem.declarerDirection]);

  const dummyPlayedCards = useMemo(() => {
    return playedCardCodes.filter(code => {
      const card = animation.cardsPlayedSoFar.find(pc => pc.card.toUpperCase() === code);
      return card && card.direction === problem.dummyDirection;
    });
  }, [playedCardCodes, animation.cardsPlayedSoFar, problem.dummyDirection]);

  const { nsTricksWon, ewTricksWon } = useMemo(() => {
    if (animation.currentCardIndex < 0) return { nsTricksWon: 0, ewTricksWon: 0 };
    const completedTrickCount = Math.floor((animation.currentCardIndex + 1) / 4);
    let ns = 0;
    let ew = 0;
    for (let i = 0; i < completedTrickCount && i < problem.trickWinners.length; i++) {
      const winner = problem.trickWinners[i];
      if (winner === 'N' || winner === 'S') {
        ns++;
      } else {
        ew++;
      }
    }
    return { nsTricksWon: ns, ewTricksWon: ew };
  }, [animation.currentCardIndex, problem.trickWinners]);

  const gameLastIndex = (problem.totalTricksPlayed ?? 13) * 4 - 1;
  const canGoPrevCard = animation.currentCardIndex >= 0;
  const canGoPrevTrick = animation.currentCardIndex >= 0;
  const canGoNextCard = animation.currentCardIndex < gameLastIndex;
  const canGoNextTrick = animation.currentCardIndex < gameLastIndex;

  const handlePlayStop = useCallback(() => {
    if (animation.animationState === 'PLAYING') {
      animation.stop();
    } else {
      animation.play();
    }
  }, [animation]);

  const renderHand = (handInfo: CountingTableSeat | null, stretch?: boolean, mobile?: boolean) => {
    if (!handInfo) return null;
    return (
      <PlayableHandDisplay
        hand={handInfo.hand}
        playedCards={handInfo.direction === problem.declarerDirection ? declarerPlayedCards : dummyPlayedCards}
        label={mobile ? handInfo.mobileLabel : handInfo.label}
        stretch={stretch}
        removePlayedCards
      />
    );
  };

  const renderAuction = () => !showAuction ? null : (
    <div className="counting-auction-container border border-line rounded-lg px-2 py-1">
      <h2
        className="font-semibold text-ink mb-0.5 counting-auction-header"
        style={{ fontSize: 'clamp(0.65rem, 1.3vw, 0.875rem)' }}
      >
        Auction
      </h2>
      <div className="counting-auction-scroll scrollable" style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.95rem)' }}>
        <AuctionTable
          auction={shortAuction}
          vulnerability={problem.vulnerability}
          decisionSeat={problem.declarerDirection}
          abbreviatePass
          compact
        />
      </div>
    </div>
  );

  // ─── The rebuilt Counting table ────────────────────────────────────────────────────────────
  // Everything above is state and bookkeeping, which both tables need; everything below is the
  // pre-028 arrangement, which only the legacy consumers draw.
  if (isTableSession) {
    return (
      <CountingTable
        problem={problem}
        animation={animation}
        seats={seats}
        declarerPlayedCards={declarerPlayedCards}
        dummyPlayedCards={dummyPlayedCards}
        nsTricksWon={nsTricksWon}
        ewTricksWon={ewTricksWon}
        canGoPrevTrick={canGoPrevTrick}
        canGoPrevCard={canGoPrevCard}
        canGoNextCard={canGoNextCard}
        canGoNextTrick={canGoNextTrick}
        onPlayStop={handlePlayStop}
        showClaimed={showClaimed}
        showAuction={showAuction}
      />
    );
  }

  return (
    // White PlayTable container: uniform ~5px inner padding across breakpoints.
    <div className="bg-card border border-line-strong rounded-lg narrow:rounded p-[5px]">
      {/* === Desktop layout: 3-col x 3-row grid === */}
      <div className="hidden md:flex narrow:hidden justify-center overflow-hidden">
        <div
          className="grid gap-2 short:gap-1"
          style={{
            gridTemplateColumns: 'auto auto auto',
            gridTemplateRows: 'auto auto auto',
          }}
        >
          {/* Row 1, Col 1: Vulnerability indicator — top-aligned so the
              indicator's top matches the Auction container's top in Col 3. */}
          <div className="row-start-1 col-start-1 flex items-start justify-center">
            <VulnerabilityIndicator
              vulnerability={problem.vulnerability}
              vulnerabilityText={problem.vulnerabilityText}
              dealer={problem.dealDirection}
            />
          </div>

          {/* Row 1-2, Col 3: Auction top-aligned, right hand bottom-aligned with left */}
          <div className="row-start-1 row-span-2 col-start-3 flex flex-col justify-between counting-desktop-col3">
            {showAuction && <div>{renderAuction()}</div>}
            <div className="flex items-center">{renderHand(rightHand, true)}</div>
          </div>

          {/* Row 2, Col 1: Left hand — bottom-aligned to match right hand */}
          <div className="row-start-2 col-start-1 flex items-end justify-center">
            {renderHand(leftHand)}
          </div>

          {/* Row 2, Col 2: Played cards + Initial Play button + Declarer label */}
          <div className="row-start-2 col-start-2 flex flex-col items-center justify-center py-1 short:py-0.5 relative">
            <div className="relative">
              <PlayedCardsDisplay trickCards={animation.currentTrickCards} />
            </div>
            <span className="text-ink text-sm mt-2">{declarerLabel}</span>
            {animation.animationState === 'INITIAL' && (
              <InitialPlayButton onClick={animation.startInitial} />
            )}
          </div>

          {/* Row 3: Bottom controls */}
          <div className="row-start-3 col-start-1 flex items-center justify-center pt-0.5">
            <TrickCountIndicator
              nsTricksWon={nsTricksWon}
              ewTricksWon={ewTricksWon}
              contract={problem.contract}
              contractDirection={problem.contractDirection}
            />
          </div>
          {showControls && (
            <div className="row-start-3 col-start-2 flex items-center justify-center pt-0.5">
              <TrickNavigationBar
                animationState={animation.animationState}
                canGoPrevTrick={canGoPrevTrick}
                canGoPrevCard={canGoPrevCard}
                canGoNextCard={canGoNextCard}
                canGoNextTrick={canGoNextTrick}
                onPrevTrick={animation.prevTrick}
                onPrevCard={animation.prevCard}
                onPlayStop={handlePlayStop}
                onNextCard={animation.nextCard}
                onNextTrick={animation.nextTrick}
                disabled={animation.animationState === 'INITIAL'}
                hasReachedStopTrick={animation.hasReachedStopTrick}
              />
            </div>
          )}
          {showControls && (
            <div className="row-start-3 col-start-3 flex items-center justify-center pt-0.5">
              <AnimationSpeedControl
                speed={animation.speed}
                onSpeedChange={animation.setSpeed}
              />
            </div>
          )}
        </div>
      </div>

      {/* === Narrow/Mobile layout: compact stacked === */}
      <div className="md:hidden narrow:block counting-mobile-layout">
        {isShortPhone && !isHowardPage ? (
          <>
            {/* Short-phone layout (iPhone SE class) — save vertical space */}
            {/* Row 1: [Vuln + Contract/Count] | [AnimSpeed + TrickNav] | [Auction (scrollable)] */}
            <div className="flex gap-1 mb-1.5 items-start">
              <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                <VulnerabilityIndicator
                  vulnerability={problem.vulnerability}
                  vulnerabilityText={problem.vulnerabilityText}
                  dealer={problem.dealDirection}
                  compact
                />
                <TrickCountIndicator
                  nsTricksWon={nsTricksWon}
                  ewTricksWon={ewTricksWon}
                  contract={problem.contract}
                  contractDirection={problem.contractDirection}
                />
              </div>
              {showControls && (
                <div className="flex-1 flex flex-col items-center justify-center gap-[5px]">
                  <AnimationSpeedControl
                    speed={animation.speed}
                    onSpeedChange={animation.setSpeed}
                  />
                  <TrickNavigationBar
                    animationState={animation.animationState}
                    canGoPrevTrick={canGoPrevTrick}
                    canGoPrevCard={canGoPrevCard}
                    canGoNextCard={canGoNextCard}
                    canGoNextTrick={canGoNextTrick}
                    onPrevTrick={animation.prevTrick}
                    onPrevCard={animation.prevCard}
                    onPlayStop={handlePlayStop}
                    onNextCard={animation.nextCard}
                    onNextTrick={animation.nextTrick}
                    disabled={animation.animationState === 'INITIAL'}
                    hasReachedStopTrick={animation.hasReachedStopTrick}
                  />
                </div>
              )}
              {showAuction && (
                <div className="flex-shrink-0">
                  <div className="counting-auction-fit counting-auction-short-phone">{renderAuction()}</div>
                </div>
              )}
            </div>

            {/* Row 2: You hold | Played cards | Dummy */}
            <div className="flex items-stretch gap-2 mb-1.5">
              <div className="flex-1 min-w-0 counting-mobile-hand">
                {declarerHand && renderHand(declarerHand, false, true)}
              </div>
              <div className="flex-shrink-0 flex flex-col items-center justify-center">
                <div className="counting-short-phone-cards relative">
                  <PlayedCardsDisplay trickCards={animation.currentTrickCards} compact />
                  {animation.animationState === 'INITIAL' && (
                    <InitialPlayButton onClick={animation.startInitial} />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0 counting-mobile-hand">
                {dummyHandInfo && renderHand(dummyHandInfo, false, true)}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Standard mobile layout — horizontal hand arrangement */}
            {/* Row 1: Vulnerability + Auction side by side.
                flex-1/min-h-0 retained for narrow-landscape fallback (Fold7
                chrome-/native-unfolded-pt at 749×654/702 — narrow but landscape,
                needing the original Row-1 grow behavior). The portrait media
                query for .counting-auction-row overrides to flex: 0 1 auto. */}
            <div className="flex gap-1.5 mb-2 flex-1 min-h-0 items-stretch counting-auction-row">
              <div className="flex-shrink-0 flex flex-col items-center justify-start">
                <VulnerabilityIndicator
                  vulnerability={problem.vulnerability}
                  vulnerabilityText={problem.vulnerabilityText}
                  dealer={problem.dealDirection}
                  compact
                />
              </div>
              {showAuction && (
                <div className="flex-1 min-w-0 flex items-stretch">
                  <div className="counting-auction-fit">{renderAuction()}</div>
                </div>
              )}
            </div>

            {/* Row 2: Declarer hand | Played cards | Dummy hand */}
            <div className="flex items-stretch gap-2 mb-2 counting-hands-row">
              <div className="flex-1 min-w-0 counting-mobile-hand">
                {declarerHand && renderHand(declarerHand, false, true)}
              </div>
              <div className="flex-shrink-0 flex flex-col items-center justify-center">
                <div className="relative">
                  <PlayedCardsDisplay trickCards={animation.currentTrickCards} compact />
                  {animation.animationState === 'INITIAL' && (
                    <InitialPlayButton onClick={animation.startInitial} />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0 counting-mobile-hand">
                {dummyHandInfo && renderHand(dummyHandInfo, false, true)}
              </div>
            </div>

            {/* Row 3: Trick count | Nav bar | Speed control */}
            <div className="flex items-center justify-between gap-1 counting-controls-row">
              <div className="flex-shrink-0">
                <TrickCountIndicator
                  nsTricksWon={nsTricksWon}
                  ewTricksWon={ewTricksWon}
                  contract={problem.contract}
                  contractDirection={problem.contractDirection}
                />
              </div>
              {showControls && (
                <div className="flex-shrink-0">
                  <TrickNavigationBar
                    animationState={animation.animationState}
                    canGoPrevTrick={canGoPrevTrick}
                    canGoPrevCard={canGoPrevCard}
                    canGoNextCard={canGoNextCard}
                    canGoNextTrick={canGoNextTrick}
                    onPrevTrick={animation.prevTrick}
                    onPrevCard={animation.prevCard}
                    onPlayStop={handlePlayStop}
                    onNextCard={animation.nextCard}
                    onNextTrick={animation.nextTrick}
                    disabled={animation.animationState === 'INITIAL'}
                    hasReachedStopTrick={animation.hasReachedStopTrick}
                  />
                </div>
              )}
              {showControls && (
                <div className="flex-shrink-0">
                  <AnimationSpeedControl
                    speed={animation.speed}
                    onSpeedChange={animation.setSpeed}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
